import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  Loader2,
  Mail,
  ShieldAlert,
  X,
} from "lucide-react";
import { useAronWorkModes, useExecuteAronWrite, useRejectAronWrite } from "../api/aronApi";
import type { AronPendingWrite, AronWriteScopeEntry } from "../types";

function StatusBadge({ status }: { status: AronPendingWrite["status"] }) {
  const map: Record<AronPendingWrite["status"], { label: string; cls: string }> = {
    pending: { label: "Czeka na zatwierdzenie", cls: "bg-amber-100 text-amber-800" },
    approved: { label: "Zatwierdzanie…", cls: "bg-amber-100 text-amber-800" },
    executed: { label: "Utworzono", cls: "bg-green-100 text-green-800" },
    failed: { label: "Błąd", cls: "bg-red-100 text-red-800" },
    rejected: { label: "Odrzucono", cls: "bg-gray-100 text-gray-600" },
    withdrawn: { label: "Wycofano (ARON)", cls: "bg-gray-100 text-gray-600" },
  };
  const s = map[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-28 shrink-0 text-gray-400">{label}</span>
      <span className="min-w-0 text-gray-700 dark:text-gray-200">{children}</span>
    </div>
  );
}

/**
 * Approval card for a kind=schedule_task pending write — the chat-side creation
 * of a recurring task. Approving it creates the AronScheduledTask (never touches
 * the bridge); tasks with a write_scope get an explicit warning block, because
 * approval authorizes AUTONOMOUS writes in future runs.
 */
export function AronScheduledTaskProposal({
  conversationId,
  write,
  onResolved,
  readOnly = false,
}: {
  conversationId: string;
  write: AronPendingWrite;
  onResolved?: () => void;
  /** Podgląd cudzej rozmowy (audyt) — karta bez przycisków decyzji. */
  readOnly?: boolean;
}) {
  const execute = useExecuteAronWrite();
  const reject = useRejectAronWrite();
  const { data: workModes } = useAronWorkModes();
  const [confirmScope, setConfirmScope] = useState(false);
  const busy = execute.isPending || reject.isPending;
  const pending = write.status === "pending";

  const body = write.body as {
    name?: string;
    instruction?: string;
    cron_human?: string;
    cron_expression?: string;
    reasoning_mode?: string;
    email_to?: string[];
    email_cc?: string[];
    write_scope?: AronWriteScopeEntry[];
    max_writes_per_run?: number;
    visibility?: string;
  };
  const scope = body.write_scope ?? [];
  const shared = body.visibility === "shared";
  const summary = (write.response_summary ?? {}) as {
    denied_reason?: string;
    approval_status?: string;
  };
  const failedReason =
    write.status === "failed"
      ? summary.denied_reason ||
        (write.response_status
          ? `Serwer odrzucił zapis (kod ${write.response_status}), nie podając treści błędu.`
          : "Zapis się nie powiódł. Szczegóły z serwera jeszcze nie dotarły.")
      : "";

  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/20">
      <div className="flex items-start gap-2 px-3 py-2">
        <CalendarClock size={15} className="mt-0.5 shrink-0 text-red-600" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              Zadanie cykliczne: {body.name || "—"}
            </span>
            <StatusBadge status={write.status} />
          </div>
          {write.description && (
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {write.description}
            </p>
          )}

          <div className="mt-2 space-y-1">
            <Row label="Harmonogram">
              {body.cron_human || body.cron_expression || "—"}
            </Row>
            <Row label="Polecenie">{body.instruction || "—"}</Row>
            <Row label="Model / tryb">
              {(() => {
                const mode = body.reasoning_mode || "standard";
                const m = workModes?.reasoning_modes?.find((w) => w.value === mode);
                return m ? `${m.label} · ${m.model} · ${m.detail} (${m.cost})` : mode;
              })()}
            </Row>
            <Row label="Raport do">
              <span className="inline-flex items-center gap-1">
                <Mail size={11} />
                {(body.email_to ?? []).join(", ") || "—"}
                {(body.email_cc ?? []).length > 0 && ` (DW: ${body.email_cc!.join(", ")})`}
              </span>
            </Row>
            {shared && (
              <Row label="Widoczność">
                współdzielone — wyniki zobaczy wskazana publiczność
                {write.status === "pending" && " (u nie-CEO wymaga akceptacji CEO)"}
              </Row>
            )}
          </div>

          {scope.length > 0 && (
            <div className="mt-2 rounded-md border border-red-300 bg-red-100/60 p-2 dark:border-red-800 dark:bg-red-900/30">
              <div className="flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-300">
                <ShieldAlert size={12} /> Zapisy automatyczne — bez pytania o zgodę
              </div>
              <ul className="mt-1 list-inside list-disc text-[11px] text-red-700/90 dark:text-red-300/90">
                {scope.map((s, i) => (
                  <li key={i} className="font-mono">
                    {s.method} {s.path_template}
                    {s.allowed_fields?.length ? ` (pola: ${s.allowed_fields.join(", ")})` : ""}
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] text-red-700/80 dark:text-red-300/80">
                Zatwierdzenie autoryzuje te operacje w każdym przebiegu (limit{" "}
                {body.max_writes_per_run ?? 20} na przebieg).
              </div>
            </div>
          )}

          {failedReason && (
            <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle size={12} /> {failedReason}
            </p>
          )}
          {write.status === "executed" && (
            <div className="mt-1.5 text-xs">
              {summary.approval_status === "pending_ceo" ? (
                <span className="text-amber-700 dark:text-amber-300">
                  Zadanie utworzone — czeka na akceptację CEO.
                </span>
              ) : (
                <span className="text-green-700 dark:text-green-300">
                  Zadanie utworzone i zaplanowane.
                </span>
              )}{" "}
              <Link to="/aron/tasks" className="text-red-600 hover:underline">
                Otwórz Zadania cykliczne →
              </Link>
            </div>
          )}
        </div>
      </div>

      {pending && !readOnly && (
        <div className="border-t border-red-200/70 px-3 py-2 dark:border-red-900/40">
          {scope.length > 0 && (
            <label className="mb-2 flex cursor-pointer items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={confirmScope}
                onChange={(e) => setConfirmScope(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              Rozumiem, że powyższe zapisy będą wykonywane automatycznie, bez
              każdorazowego zatwierdzania.
            </label>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                reject.mutate(
                  { conversationId, writeId: write.id },
                  { onSuccess: () => onResolved?.() },
                )
              }
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {reject.isPending ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
              Odrzuć
            </button>
            <button
              type="button"
              disabled={busy || (scope.length > 0 && !confirmScope)}
              onClick={() =>
                execute.mutate(
                  { conversationId, writeId: write.id },
                  { onSuccess: () => onResolved?.() },
                )
              }
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {execute.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Zatwierdź zadanie
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
